import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';

import { IRecipe, IRecipeDevices, IRecipeOverview } from '@seb/web-design-system/recipe-overview';
import { PositionEnum, ThemesEnum } from '@seb/web-design-system/core';

import { RecipeService } from '../services/recipe.service';
import { Recipe, RecipeKitchenware } from '../models';

@Injectable({
  providedIn: 'root'
})
export class RecipeMapper {
  constructor(
    private translateService: TranslateService,
    private recipeService: RecipeService
  ) {}

  recipeToIRecipe(recipe: Recipe): IRecipe {
    return {
      title: recipe.title,
      communityTopRecipe: recipe.communityTopRecipe
        ? {
          raters: recipe.communityTopRecipe?.raters,
          rating: recipe.communityTopRecipe?.rating,
          comments: recipe.communityTopRecipe?.comments
        }
        : undefined,
      yield: recipe.yield
        ? {
          unit: {
            name: recipe.yield?.unit?.name,
            abbreviation: recipe.yield?.unit?.abbreviation ? recipe.yield?.unit?.abbreviation : '',
            pluralName: recipe.yield?.unit?.pluralName
          },
          quantity: recipe.yield?.quantity
        }
        : undefined,
      ingredients:
        recipe?.ingredients?.length > 0
          ? recipe?.ingredients?.map((ingredient) => this.recipeService.extractIngredient(ingredient))
          : [],
      variants: recipe?.variants?.length > 0 ? recipe.variants.map((variant) => this.recipeToIRecipe(variant)) : [],
      kitchenwares:
        recipe?.kitchenwares?.length > 0
          ? recipe.kitchenwares.map((kitchenware: RecipeKitchenware) =>
            this.recipeService.extractKitchenware(kitchenware)
          )
          : [],
      steps: recipe?.steps?.length > 0 ? recipe?.steps.map((step) => this.recipeService.extractStep(step)) : [],
      isAutomaticallyGenerated: recipe?.isAutomaticallyGenerated
    };
  }

  recipeToRecipeOverview(recipe: Recipe, recipeDevices?: IRecipeDevices): IRecipeOverview {
    return {
      recipe: this.recipeToIRecipe(recipe),
      user: this.recipeService.extractUser(recipe),
      media: this.recipeService.extractCover(recipe),
      timeOverview: {
        totalTime: this.recipeService.formatRecipeDuration(recipe.durations?.totalTime || 0),
        preparation: {
          icon: 'icon-prep-duration',
          text: this.recipeService.formatRecipeDuration(recipe.durations?.prepTime || 0)
        },
        cooking: {
          icon: 'icon-cooking-duration',
          text: this.recipeService.formatRecipeDuration(recipe.durations?.cookingTime || 0)
        },
        cooling: {
          icon: 'icon-rest-duration',
          text: this.recipeService.formatRecipeDuration(recipe.durations?.restingTime || 0)
        }
      },
      recipeDevices: recipeDevices,
      yieldOptions: this.recipeService.extractYields(recipe),
      yieldSelectable: true,
      tags: recipe?.isReworked
        ? [
          {
            text: this.translateService.instant('recipes.tag.recipeImproved.label'),
            theme: ThemesEnum.promo2,
            iconPos: PositionEnum.left,
            media: {
              altText: this.translateService.instant('recipes.tag.recipeImproved.label'),
              url: this.translateService.instant('recipes.tag.recipeImproved.icon'),
              mime: 'image'
            }
          }
        ]
        : []
    };
  }
}
